import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';

import Avatar from '@/components/Avatar';
import Table from '@/components/Table';
import { TableCell, TableHead, TableHeading, TableRow } from '@/elements/Table';
import styled from '@/styles';
import type { TierBoundaryLite, WorkerListItem } from '@/services/workersV2API';
import TierPill from '@/routes/Agency/WorkerV2/components/dependabilityV2/TierPill';
import { isWorkerSuspended } from '@/util/dependabilityBadge';

import CurrentStatusPill from './CurrentStatusPill';
import HeaderTooltip from './HeaderTooltip';
import StatusIcon from './StatusIcon';
import TierLegend from './TierLegend';
import {
  bgCheckCleared,
  formatActiveAgo,
  formatPhone,
  formatTaxType,
  i9Cleared,
  i9IconLabel,
  TIER_OPTIONS,
} from '../mappings';

export type SortColumn =
  | 'id'
  | 'checkr_status'
  | 'i9_status'
  | 'average_rating'
  | 'tax_type'
  | 'shifts'
  | 'created_at'
  // GRAVY-10975: the Onboarding tab's step position and days stuck, and its
  // Shifts column, which shows the completed count alone (review D-07).
  | 'onboarding_step'
  | 'stuck_for'
  | 'shifts_completed'
  // GRAVY-10983: graphy's status-ranked sorts for the Background check and I-9
  // columns. `checkr_status` / `i9_status` order by the raw vendor value, so
  // rows with one label were split up by values the screen never shows.
  | 'background_check_status'
  | 'i9_status_rank';
export type SortState = { by: SortColumn; direction: 'asc' | 'desc' };

const Th = styled(TableHeading, {
  padding: '12px 8px',
  textAlign: 'left',
  fontSize: '12px',
  fontWeight: 600,
  color: '#3D3D3D',
  whiteSpace: 'nowrap',
  variants: { sortable: { true: { cursor: 'pointer', userSelect: 'none' } } },
});
const Td = styled(TableCell, {
  padding: '16px 8px',
  verticalAlign: 'top',
  fontSize: '14px',
});
const Name = styled('button', {
  appearance: 'none',
  border: 'none',
  background: 'transparent',
  padding: 0,
  font: 'inherit',
  fontWeight: 600,
  cursor: 'pointer',
  textAlign: 'left',
});
const Muted = styled('div', { fontSize: '13px', color: '#5A5A5A' });
const ActiveChip = styled('span', {
  display: 'inline-block',
  marginTop: '6px',
  padding: '2px 8px',
  borderRadius: '6px',
  fontSize: '12px',
  backgroundColor: '#EDEDED',
  color: '#5A5A5A',
  variants: {
    today: { true: { backgroundColor: '#E7F4E6', color: '#1E8E3E' } },
  },
});
const Unscored = styled('span', { color: '#8A8A8A' });
// Also the Onboarding tabs' empty text while a search or filter is set
// (BH-68), so every tab words a "nothing matched" list the same way.
export const NO_MATCH_TEXT = 'No workers match your search.';

const Empty = styled('div', {
  padding: '48px 16px',
  textAlign: 'center',
  color: '#5A5A5A',
});

const HEADERS: Array<{
  label: string;
  sortKey?: SortColumn;
  tooltip?: 'dependability' | 'shifts';
}> = [
  { label: 'User Information', sortKey: 'id' },
  { label: 'BG check', sortKey: 'checkr_status' },
  { label: 'I-9', sortKey: 'i9_status' },
  { label: 'Rating', sortKey: 'average_rating' },
  { label: 'Dependability', tooltip: 'dependability' },
  { label: 'Tax type', sortKey: 'tax_type' },
  // GRAVY-10835: sorts by the claimed/completed pair the cell shows.
  { label: 'Shifts', sortKey: 'shifts', tooltip: 'shifts' },
  { label: 'Current status' },
];

export const SORT_COLUMNS = HEADERS.flatMap((h) =>
  h.sortKey ? [h.sortKey] : [],
);

type Props = {
  workers: WorkerListItem[];
  sort: SortState;
  onSort: (column: SortColumn) => void;
  tierBoundaries: TierBoundaryLite[];
  onWorkerClick: (id: number) => void;
  loading?: boolean;
};

export const SortIcon = ({
  active,
  direction,
}: {
  active: boolean;
  direction: 'asc' | 'desc';
}) => {
  if (!active)
    return (
      <UnfoldMoreIcon
        sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle', opacity: 0.35 }}
      />
    );
  return direction === 'asc' ? (
    <ArrowUpwardIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />
  ) : (
    <ArrowDownwardIcon
      sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }}
    />
  );
};

const WorkersTable = ({
  workers,
  sort,
  onSort,
  tierBoundaries,
  onWorkerClick,
  loading = false,
}: Props) => (
  <Table>
    <TableHead>
      <TableRow>
        {HEADERS.map((h) => (
          <Th
            key={h.label}
            sortable={Boolean(h.sortKey)}
            aria-sort={
              h.sortKey && sort.by === h.sortKey
                ? sort.direction === 'asc'
                  ? 'ascending'
                  : 'descending'
                : undefined
            }
            onClick={h.sortKey ? () => onSort(h.sortKey!) : undefined}
          >
            {h.label}
            {h.tooltip === 'dependability' && (
              <HeaderTooltip
                testId="depTierTooltipBtn"
                label="About dependability tiers"
                title="Dependability tiers"
                kind="tiers"
              >
                <TierLegend boundaries={tierBoundaries} />
              </HeaderTooltip>
            )}
            {h.tooltip === 'shifts' && (
              <HeaderTooltip
                testId="shiftsTooltipBtn"
                label="About shifts"
                title="Shifts (claimed / completed)"
                kind="shifts"
              >
                <p>
                  <strong>Claimed</strong> shifts are accepted shifts from the
                  job board
                </p>
                <p>
                  <strong>Completed</strong> shifts are shifts worked through to
                  the end
                </p>
              </HeaderTooltip>
            )}
            {h.sortKey && (
              <SortIcon
                active={sort.by === h.sortKey}
                direction={sort.direction}
              />
            )}
          </Th>
        ))}
      </TableRow>
    </TableHead>
    {/* Old rows during a reload, as on the column tabs (review R5B-05, R6A-06). */}
    <tbody
      data-testid="tableBody"
      aria-busy={loading && workers.length > 0 ? true : undefined}
      style={loading && workers.length > 0 ? { opacity: 0.5 } : undefined}
    >
      {workers.length === 0 && loading && (
        <tr>
          <td colSpan={HEADERS.length}>
            <Empty data-testid="listLoading" aria-busy="true">
              Loading workers…
            </Empty>
          </td>
        </tr>
      )}
      {workers.length === 0 && !loading && (
        <tr>
          <td colSpan={HEADERS.length}>
            <Empty data-testid="emptyState">{NO_MATCH_TEXT}</Empty>
          </td>
        </tr>
      )}
      {workers.map((w) => {
        const activeAgo = formatActiveAgo(w.last_active_at);
        const tier = w.dependability_v2?.tier;
        const suspended = isWorkerSuspended(
          w.accountability_status,
          w.is_suspended,
        );
        const tierLabel =
          tierBoundaries.find((b) => b.tier === tier)?.label ??
          TIER_OPTIONS.find((t) => t.value === tier)?.label ??
          '';
        return (
          <TableRow key={w.id} data-worker-id={w.id}>
            <Td>
              <div style={{ display: 'flex', gap: '12px' }}>
                <Avatar
                  src={w.avatar_url}
                  firstName={w.user?.first_name}
                  lastName={w.user?.last_name}
                  a11yLabel={w.user?.full_name ?? 'Worker avatar'}
                />
                <div>
                  <Name type="button" onClick={() => onWorkerClick(w.id)}>
                    {w.user?.full_name ??
                      `${w.user?.first_name ?? ''} ${w.user?.last_name ?? ''}`.trim()}
                  </Name>
                  <Muted>{formatPhone(w.user?.phone_number)}</Muted>
                  <ActiveChip today={activeAgo === 'active today'}>
                    {activeAgo}
                  </ActiveChip>
                </div>
              </div>
            </Td>
            <Td>
              <StatusIcon
                cleared={bgCheckCleared(w)}
                testId={`bgCheckIcon-${w.id}`}
                label={
                  bgCheckCleared(w)
                    ? 'Background check cleared'
                    : 'Background check pending'
                }
              />
            </Td>
            <Td>
              <StatusIcon
                cleared={i9Cleared(w)}
                testId={`i9Icon-${w.id}`}
                label={i9IconLabel(w, 'I-9 pending')}
              />
            </Td>
            <Td>
              {w.average_rating === null || w.average_rating === undefined
                ? '—'
                : w.average_rating.toFixed(1)}
            </Td>
            <Td>
              {tier || suspended ? (
                <TierPill
                  tier={tier ?? ''}
                  label={tierLabel}
                  accountabilityStatus={w.accountability_status}
                  isSuspended={w.is_suspended}
                />
              ) : (
                <Unscored data-testid={`tierUnscored-${w.id}`}>—</Unscored>
              )}
            </Td>
            <Td>{formatTaxType(w.tax_type)}</Td>
            <Td>
              {w.shifts?.claimed ?? 0}/{w.shifts?.completed ?? 0}
            </Td>
            <Td>
              {w.current_status ? (
                <CurrentStatusPill status={w.current_status} />
              ) : (
                '—'
              )}
            </Td>
          </TableRow>
        );
      })}
    </tbody>
  </Table>
);

export default WorkersTable;
